import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import '../styles/segment.css';
import SegmentFlair, { FlairDropdown } from './SegmentFlair';
import { getFlairsForSegmentType, getVisibleAndOverflowFlairs } from '../utils/flairUtils';

const Segment = ({ 
  children, 
  borderColor = '#ff0000', 
  backgroundColor = '#ffffff', // New prop for background color
  className = '',
  variant = 'default', // 'primary', 'secondary', 'text', 'background', 'default'
  segmentType = '', // Type of segment for context-aware flairs
  onFlairAction = () => {}, // Callback for flair actions
  onDelete = null, // Callback for delete action
  isSelected = false, // Whether segment is selected
  isExpanded = false, // Whether the segment's track is expanded
  ...props 
}) => {
  // Map borderColor to CSS class or use custom color
  const getBorderClass = () => {
    const colorMap = {
      '#00A9E0': 'border-primary',
      '#92C911': 'border-secondary', 
      '#E87722': 'border-text',
      '#CE0F69': 'border-background',
      '#ff0000': 'border-default'
    };
    
    return colorMap[borderColor] || 'border-default';
  };

  const segmentClasses = [
    'segment-component',
    getBorderClass(),
    className
  ].filter(Boolean).join(' ');

  // If using a custom border color not in our predefined set, use inline style
  const customBarColorStyle = !['#00A9E0', '#92C911', '#E87722', '#CE0F69', '#ff0000'].includes(borderColor) 
    ? { backgroundColor: borderColor } 
    : {};

  return (
    <Box
      className={`${segmentClasses} ${isSelected ? 'selected' : ''}`}
      sx={{ backgroundColor: backgroundColor }} // Apply the background color here
      {...props}
    >
      {/* Floating bar */}
      <Box 
        className={`segment-floating-bar ${getBorderClass()}`} 
        style={customBarColorStyle} 
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', position: 'relative' }}>
        {/* Main content */}
        {typeof children === 'string' ? (
          <Typography 
            className="segment-text"
            variant="body2"
          >
            {children}
          </Typography>
        ) : (
          children
        )}
        
        {/* Flairs */}
        {segmentType && (
          <div 
            className={`segment-flairs-container ${isExpanded ? 'expanded' : ''}`} 
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const flairs = getFlairsForSegmentType(segmentType, borderColor);
              const { visibleFlairs, overflowFlairs } = getVisibleAndOverflowFlairs(flairs, 4);
              
              return (
                <>
                  {visibleFlairs.map((flair, index) => (
                    <SegmentFlair
                      key={index}
                      label={flair.label}
                      color={flair.color}
                      onClick={() => onFlairAction(flair.action, segmentType)}
                    />
                  ))}
                  
                  {overflowFlairs.length > 0 && (
                    <FlairDropdown
                      flairs={overflowFlairs}
                      onSelect={(action) => onFlairAction(action, segmentType)}
                    />
                  )}
                  
                  {/* Delete button next to flairs */}
                  {onDelete && (
                    <IconButton 
                      className="segment-delete-button"
                      size="small"
                      sx={{ 
                        marginLeft: '8px',
                        padding: '4px',
                        color: '#757575',
                        '&:hover': {
                          color: '#f44336',
                          backgroundColor: 'rgba(244, 67, 54, 0.08)'
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm('Are you sure you want to delete this segment?')) {
                          onDelete();
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </Box>
    </Box>
  );
};

export default Segment;
